import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@components/ui/button';

interface BillFormData {
  vendorId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  amount: string;
  description: string;
  terms: string;
  memo: string;
}

/**
 * BillCreatePage Component
 * Form to create a new accounts payable bill
 */
export function BillCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<BillFormData>({
    vendorId: '',
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    amount: '',
    description: '',
    terms: 'net-30',
    memo: '',
  });

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    // TODO: Implement bill creation
    console.log('Creating bill:', formData);
    navigate('/accounts-payable/bills');
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Mock vendors for demo
  const vendors = [
    { id: 'v1', name: 'Acme Supplies Inc.' },
    { id: 'v2', name: 'Tech Solutions LLC' },
    { id: 'v3', name: 'CloudHost Services' },
    { id: 'v4', name: 'Legal Partners LLP' },
    { id: 'v5', name: 'Marketing Pro Agency' },
  ];

  return (
    <div className="container py-8">
      {/* Page Header */}
      <div className="mb-8">
        <Link
          to="/accounts-payable/bills"
          className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Bills
        </Link>
        <h2 className="text-2xl font-bold">Create New Bill</h2>
        <p className="mt-1 text-muted-foreground">Enter vendor invoice details</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mx-auto max-w-3xl">
        <div className="rounded-xl border bg-card">
          {/* Vendor Selection */}
          <div className="border-b p-6">
            <h3 className="mb-4 font-semibold">Vendor Information</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="vendorId" className="mb-1.5 block text-sm font-medium">
                  Vendor *
                </label>
                <select
                  id="vendorId"
                  name="vendorId"
                  value={formData.vendorId}
                  onChange={handleChange}
                  required
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select a vendor</option>
                  {vendors.map((vendor) => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <Link to="/accounts-payable/vendors" className="text-sm text-primary hover:underline">
                  + Add New Vendor
                </Link>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="border-b p-6">
            <h3 className="mb-4 font-semibold">Invoice Details</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="invoiceNumber" className="mb-1.5 block text-sm font-medium">
                  Invoice Number *
                </label>
                <input
                  type="text"
                  id="invoiceNumber"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleChange}
                  required
                  placeholder="e.g., INV-2026-001"
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label htmlFor="invoiceDate" className="mb-1.5 block text-sm font-medium">
                  Invoice Date *
                </label>
                <input
                  type="date"
                  id="invoiceDate"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={handleChange}
                  required
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label htmlFor="dueDate" className="mb-1.5 block text-sm font-medium">
                  Due Date *
                </label>
                <input
                  type="date"
                  id="dueDate"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  required
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label htmlFor="terms" className="mb-1.5 block text-sm font-medium">
                  Payment Terms
                </label>
                <select
                  id="terms"
                  name="terms"
                  value={formData.terms}
                  onChange={handleChange}
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="due-on-receipt">Due on Receipt</option>
                  <option value="net-15">Net 15</option>
                  <option value="net-30">Net 30</option>
                  <option value="net-45">Net 45</option>
                  <option value="net-60">Net 60</option>
                </select>
              </div>
            </div>
          </div>

          {/* Amount and Description */}
          <div className="border-b p-6">
            <h3 className="mb-4 font-semibold">Amount</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="amount" className="mb-1.5 block text-sm font-medium">
                  Total Amount (USD) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    value={formData.amount || ''}
                    onChange={handleChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="h-10 w-full rounded-md border bg-background pl-7 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="description" className="mb-1.5 block text-sm font-medium">
                  Description
                </label>
                <input
                  type="text"
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Brief description of this bill"
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="memo" className="mb-1.5 block text-sm font-medium">
                  Internal Memo
                </label>
                <textarea
                  id="memo"
                  name="memo"
                  value={formData.memo}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Internal notes (not visible to vendor)"
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 p-6">
            <Link to="/accounts-payable/bills">
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </Link>
            <Button variant="outline" type="button">
              Save as Draft
            </Button>
            <Button type="submit">Create Bill</Button>
          </div>
        </div>
      </form>
    </div>
  );
}
